import { useMemo } from "react";
import type { PartialAgent } from "~/services/agents";
import { AssistantTurn } from "./AssistantTurn";
import { CohortStalenessProvider } from "./CohortStalenessContext";
import { UserMessage } from "./UserMessage";
import { parseCohort } from "./cohort-tool-uis";
import type { Turn } from "./useTurns";

const COHORT_TOOL_NAMES = new Set([
  "cohort_explorer_get_cohort",
  "cohort_explorer_update_cohort",
  "cohort_explorer_create_cohort",
  "cohort_explorer_delete_cohort",
]);

interface ConversationTurnsProps {
  turns: Turn[];
  // Agent identity for assistant avatars; null in read-only views that don't
  // resolve the bound agent.
  conversationAgent: PartialAgent | null;
  conversationId: string;
  // Live-only: surfaces the "Stopping" label on the in-flight turn.
  isCancelling?: boolean;
  // Read-only feedback rendering (admin review).
  readOnlyFeedback?: boolean;
  // Resume a paused human-in-the-loop tool call. Omitted in read-only views,
  // where the cards render but cannot be answered.
  onAnswer?: (toolCallId: string, answer: string) => void | Promise<void>;
  // Tool-call ids genuinely paused awaiting human input; gates answerability
  // of the HITL and fallback cards.
  awaitingToolCallIds?: ReadonlySet<string>;
}

// The single user/assistant turn dispatch shared by the live `Chat` and the
// read-only (shared / admin) views. Keeping it in one place means new turn
// kinds and per-turn rendering rules reach every surface automatically rather
// than having to be mirrored into each caller. Callers own their own scroll
// container and `<ol>` wrapper.
export function ConversationTurns({
  turns,
  conversationAgent,
  conversationId,
  isCancelling,
  readOnlyFeedback,
  onAnswer,
  awaitingToolCallIds,
}: ConversationTurnsProps) {
  const cohortIds = useMemo(() => {
    const ids = new Set<number>();
    for (const turn of turns) {
      if (turn.kind !== "assistant") continue;
      for (const tc of turn.toolCalls) {
        if (!COHORT_TOOL_NAMES.has(tc.name)) continue;
        const cohort = parseCohort(tc.result);
        if (cohort) ids.add(cohort.id);
      }
    }
    return [...ids];
  }, [turns]);

  return (
    <CohortStalenessProvider cohortIds={cohortIds}>
      {turns.map((turn) =>
        turn.kind === "user" ? (
          <UserMessage key={turn.key} text={turn.text} />
        ) : (
          <AssistantTurn
            key={turn.key}
            turn={turn}
            agent={conversationAgent}
            conversationId={conversationId}
            isCancelling={isCancelling}
            readOnlyFeedback={readOnlyFeedback}
            onAnswer={onAnswer}
            awaitingToolCallIds={awaitingToolCallIds}
          />
        )
      )}
    </CohortStalenessProvider>
  );
}
